import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext({
    product_name: "Portalwiz Talent Solutions",
    short_name: "Portalwiz",
    logo_url: null,
    primary_color: "#002FA7",
    tagline: "Screen 200 resumes in the time it took to read one.",
    support_email: null,
    refresh: () => {},
});

/**
 * Applies a company theme as CSS custom properties on <html> so every page
 * (admin, recruiter, public apply, embed) picks up branding without us
 * having to edit each component. Tailwind utilities `.text-ikb`,
 * `.bg-ikb`, `.border-ikb` already read `var(--theme-primary)`.
 */
function applyTheme(theme) {
    if (!theme) return;
    const root = document.documentElement;
    const SET = (k, v) => { if (v !== undefined && v !== null && v !== "") root.style.setProperty(k, v); };
    // Core colors
    SET("--theme-primary", theme.primary_color);
    SET("--theme-accent", theme.accent_color);
    SET("--theme-text", theme.text_color);
    SET("--theme-bg", theme.bg_color);
    SET("--theme-card", theme.card_bg);
    // Semantic colors
    SET("--theme-muted", theme.muted_color);
    SET("--theme-border", theme.border_color);
    SET("--theme-link", theme.link_color || theme.primary_color);
    SET("--theme-success", theme.success_color);
    SET("--theme-warning", theme.warning_color);
    SET("--theme-danger", theme.danger_color);
    // Surfaces
    SET("--theme-sidebar-bg", theme.sidebar_bg || theme.card_bg);
    SET("--theme-header-bg", theme.header_bg || theme.card_bg);
    SET("--theme-input-bg", theme.input_bg);
    SET("--theme-table-header-bg", theme.table_header_bg);
    SET("--theme-tooltip-bg", theme.tooltip_bg);
    SET("--theme-button-text", theme.button_text_color);
    SET("--theme-focus-ring", theme.focus_ring_color || theme.primary_color);
    // Typography
    SET("--theme-body-font", theme.body_font);
    SET("--theme-heading-font", theme.heading_font || theme.body_font);
    SET("--theme-display-font", theme.display_font || theme.heading_font || theme.body_font);
    SET("--theme-mono-font", theme.mono_font || "ui-monospace, SFMono-Regular, Menlo, monospace");
    SET("--font-display", theme.display_font || theme.heading_font || theme.body_font);
    SET("--font-mono", theme.mono_font || "ui-monospace, SFMono-Regular, Menlo, monospace");
    // Typography scale + weight
    if (theme.base_font_size) SET("--theme-base-font-size", `${theme.base_font_size}px`);
    if (theme.h1_scale) SET("--theme-h1-size", `${theme.h1_scale}em`);
    if (theme.h2_scale) SET("--theme-h2-size", `${theme.h2_scale}em`);
    if (theme.h3_scale) SET("--theme-h3-size", `${theme.h3_scale}em`);
    if (theme.line_height) SET("--theme-line-height", String(theme.line_height));
    if (theme.letter_spacing) {
        const ls = { tight: "-0.01em", normal: "0", wide: "0.02em" }[theme.letter_spacing] || "0";
        SET("--theme-letter-spacing", ls);
    }
    if (theme.heading_weight) SET("--theme-heading-weight", String(theme.heading_weight));
    if (theme.body_weight) SET("--theme-body-weight", String(theme.body_weight));
    // Shape / effects
    if (theme.card_radius !== undefined && theme.card_radius !== null) SET("--theme-card-radius", `${theme.card_radius}px`);
    if (theme.input_radius !== undefined && theme.input_radius !== null) SET("--theme-input-radius", `${theme.input_radius}px`);
    if (theme.shadow_depth) {
        const shadows = {
            none: "none",
            sm: "0 1px 2px rgba(15,23,42,0.06)",
            md: "0 4px 12px rgba(15,23,42,0.10)",
            lg: "0 12px 32px rgba(15,23,42,0.14)",
        };
        SET("--theme-shadow", shadows[theme.shadow_depth] || shadows.none);
    }
    // Title case + size (legacy)
    if (theme.page_title_uppercase !== undefined) root.style.setProperty("--theme-title-case", theme.page_title_uppercase ? "uppercase" : "none");
    const sizeMap = { compact: "1.8rem", default: "2.25rem", xl: "3rem" };
    if (theme.page_title_size) root.style.setProperty("--theme-title-size", sizeMap[theme.page_title_size] || sizeMap.default);
    if (theme.color_scheme) root.setAttribute("data-theme", theme.color_scheme);
    // Inject Google Fonts <link> for non-system body/heading/display fonts
    const sysFonts = new Set(["Inter", "system-ui", "sans-serif", "serif", "monospace", "Arial", "Helvetica", "Georgia", "Times", "Courier"]);
    for (const fam of [theme.body_font, theme.heading_font, theme.display_font, theme.mono_font]) {
        if (!fam) continue;
        const first = String(fam).split(",")[0].trim().replace(/^['"]|['"]$/g, "");
        if (!first || sysFonts.has(first)) continue;
        const id = `theme-font-${first.replace(/\s+/g, "-")}`;
        if (document.getElementById(id)) continue;
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first).replace(/%20/g, "+")}:wght@300;400;500;600;700;800;900&display=swap`;
        document.head.appendChild(link);
    }
    if (theme.body_font) {
        document.body.style.fontFamily = theme.body_font;
    }
    // Custom CSS escape hatch — mounted in a single <style id="theme-custom-css">
    // so subsequent updates replace instead of stacking.
    let styleTag = document.getElementById("theme-custom-css");
    if (typeof theme.custom_css === "string") {
        if (!styleTag) {
            styleTag = document.createElement("style");
            styleTag.id = "theme-custom-css";
            document.head.appendChild(styleTag);
        }
        styleTag.textContent = theme.custom_css;
    }
}

export function BrandingProvider({ children }) {
    const [branding, setBranding] = useState({
        product_name: "Portalwiz Talent Solutions",
        short_name: "Portalwiz",
        logo_url: null,
        primary_color: "#002FA7",
        tagline: "Screen 200 resumes in the time it took to read one.",
        support_email: null,
    });
    const [baseTheme, setBaseTheme] = useState(null);
    const [activeSection, setActiveSection] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/settings/branding");
            setBranding((b) => ({ ...b, ...data }));
            if (data.product_name) document.title = data.product_name;
        } catch { /* keep defaults */ }
        // Also pull the company-level theme (employer/recruiter sessions only)
        try {
            const { data } = await api.get("/company/theme");
            if (data?.theme) {
                applyTheme(data.theme);
                setBaseTheme(data.theme);
                setBranding((b) => ({
                    ...b,
                    logo_url: data.theme.logo_url || b.logo_url,
                    primary_color: data.theme.primary_color || b.primary_color,
                    theme: data.theme,
                }));
            }
        } catch { /* anon / no company yet */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Reapply base + section override whenever either changes. Section
    // overrides are just partial theme dicts that override the corresponding
    // CSS vars set by applyTheme; unsetting a section reverts to base.
    useEffect(() => {
        if (!baseTheme) return;
        const merged = activeSection && baseTheme.section_overrides?.[activeSection]
            ? { ...baseTheme, ...baseTheme.section_overrides[activeSection] }
            : baseTheme;
        applyTheme(merged);
    }, [baseTheme, activeSection]);

    const value = useMemo(() => ({
        ...branding,
        refresh: load,
        setBranding,
        applyTheme,
        setActiveSection,
        activeSection,
        baseTheme,
    }), [branding, load, activeSection, baseTheme]);

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    );
}

export function useBranding() {
    return useContext(BrandingContext);
}

/**
 * Applies a per-section theme override (from `theme.section_overrides[id]`)
 * on mount and clears it on unmount. Any page/component can opt into a
 * section-specific look (e.g., denser typography in Candidate Profile)
 * by dropping <SectionTheme id="candidate_profile" /> or calling this hook.
 */
export function useSectionTheme(sectionId) {
    const { setActiveSection } = useBranding();
    useEffect(() => {
        if (!sectionId || !setActiveSection) return () => {};
        setActiveSection(sectionId);
        return () => setActiveSection(null);
    }, [sectionId, setActiveSection]);
}

export function BrandLogo({ size = 28, className = "" }) {
    const { logo_url, short_name, primary_color } = useBranding();
    if (logo_url) {
        return <img src={logo_url} alt={short_name} style={{ height: size }} className={className} />;
    }
    return (
        <div
            className={`flex items-center justify-center ${className}`}
            style={{ width: size, height: size, backgroundColor: primary_color || "#002FA7" }}
        >
            <span className="text-white font-display font-black text-sm">{(short_name || "P")[0].toUpperCase()}</span>
        </div>
    );
}
